import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { acceptTermsV1, fetchUserInfoV1 } from 'src/api/user';
import { updateLoggedInUser } from 'src/redux/modules/authentication';
import AppButton from 'src/components/base/Button/Button';
import { UserServiceSection } from 'src/latest/src/constants/AppServerConfig';
import { Typography } from 'src/latest/src/components/Typography';
import { Logger } from 'src/latest/src/services';
import {
  NOT_ELIGIBLE,
  TERMS_ACCEPTANCE_COPY,
  TermsAcceptance,
} from './TermsAcceptanceConstant';
import {
  buildAgreementParts,
  shouldShowTermsAcceptance,
  toAcceptedDocuments,
} from './TermsAcceptanceHelper';
import './TermsAcceptanceModal.scss';

const TITLE_ID = 'terms-acceptance-title';
const DESCRIPTION_ID = 'terms-acceptance-description';

type Props = {
  // AppContainer's logout, so a 401 gets the normal session-expired handling
  onLogout: (reason: string) => void;
};

// Blocking clickwrap for legacy/dormant users who haven't accepted the 2026
// terms (ICON-5191). The BE decides eligibility and the variant; this only
// follows `loggedInUser.metadata.terms_acceptance`.
const TermsAcceptanceModal = ({ onLogout }: Props) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { pathname } = useLocation();
  const { isAuthed, loggedInUser } = useSelector(
    (state: any) => state.authentication,
  );
  const termsAcceptance: TermsAcceptance | undefined =
    loggedInUser?.metadata?.terms_acceptance;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // state alone can't stop two clicks landing before the re-render disables the button
  const isSubmittingRef = useRef(false);

  const agreementParts = useMemo(
    () => buildAgreementParts(termsAcceptance),
    [termsAcceptance],
  );
  const isEligible = shouldShowTermsAcceptance({
    isAuthed,
    termsAcceptance,
    pathname,
  });

  useEffect(() => {
    if (isEligible && !agreementParts) {
      Logger.error('terms_acceptance cannot be rendered', termsAcceptance);
    }
  }, [isEligible, agreementParts]);

  // Read at dispatch time: this runs after an await, and the profile may have
  // been refreshed elsewhere meanwhile.
  const saveTermsAcceptance = (next: TermsAcceptance) => {
    const { metadata } = (store.getState() as any).authentication.loggedInUser;

    dispatch(
      updateLoggedInUser({
        metadata: { ...metadata, terms_acceptance: next },
      }),
    );
  };

  // 409: the live documents changed under us (a tab left open across a terms
  // update). Re-read them so the user agrees to what is actually live.
  const reloadTermsAcceptance = async () => {
    try {
      const profile = await fetchUserInfoV1(
        UserServiceSection.profile_display,
        'medium',
      );

      saveTermsAcceptance(profile?.metadata?.terms_acceptance || NOT_ELIGIBLE);
      setErrorMessage(TERMS_ACCEPTANCE_COPY.termsChanged);
    } catch (error) {
      Logger.error('Failed to reload terms_acceptance', error);
      setErrorMessage(TERMS_ACCEPTANCE_COPY.submitError);
    }
  };

  const handleAgree = async () => {
    if (isSubmittingRef.current || !termsAcceptance) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await acceptTermsV1(
        toAcceptedDocuments(termsAcceptance.documents),
      );

      saveTermsAcceptance(
        typeof response?.eligible === 'boolean' ? response : NOT_ELIGIBLE,
      );
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 401) {
        onLogout('SESSION_EXPIRED');
      } else if (status === 409) {
        await reloadTermsAcceptance();
      } else {
        Logger.error('Failed to accept terms', error);
        setErrorMessage(TERMS_ACCEPTANCE_COPY.submitError);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // A payload we can't render is logged above and treated as not eligible:
  // locking the user out over a bad response is worse than asking again on
  // the next login, which the BE will do since nothing was recorded.
  if (!isEligible || !agreementParts) return null;

  return (
    <Modal
      show={true}
      backdrop="static"
      keyboard={false}
      centered={true}
      className="terms-acceptance-modal"
      backdropClassName="terms-acceptance-modal__backdrop"
      dialogClassName="terms-acceptance-modal__dialog"
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
    >
      <Modal.Header className="terms-acceptance-modal__header">
        <Typography
          id={TITLE_ID}
          variant="size-34"
          color="color-2"
          className="terms-acceptance-modal__title"
        >
          {TERMS_ACCEPTANCE_COPY.headline}
        </Typography>
      </Modal.Header>
      <Modal.Body className="terms-acceptance-modal__body">
        <div id={DESCRIPTION_ID}>
          <Typography
            variant="size-24"
            className="terms-acceptance-modal__text"
          >
            {TERMS_ACCEPTANCE_COPY.body}
          </Typography>
          <Typography
            variant="size-24"
            className="terms-acceptance-modal__text"
          >
            {agreementParts.map((part, index) =>
              'url' in part ? (
                <a
                  key={part.document}
                  href={part.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-acceptance-modal__link"
                >
                  {part.label}
                  <span className="visually-hidden">
                    {` ${TERMS_ACCEPTANCE_COPY.newTabHint}`}
                  </span>
                </a>
              ) : (
                <React.Fragment key={index}>{part.text}</React.Fragment>
              ),
            )}
          </Typography>
        </div>
        {errorMessage && (
          <p role="alert" className="terms-acceptance-modal__error">
            {errorMessage}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="terms-acceptance-modal__footer">
        <AppButton
          label={TERMS_ACCEPTANCE_COPY.agreeButton}
          onClick={handleAgree}
          isProcessing={isSubmitting}
          aria-busy={isSubmitting}
          className="terms-acceptance-modal__agree-btn"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default TermsAcceptanceModal;
